// IdCardsPanel — print student ID cards for a whole class or a hand-picked
// set of students. Cards use whatever is on the student record (photo,
// admission no., DOB, blood group, father, guardian phone, address).
import { useEffect, useState } from 'react';
import { Printer, AlertTriangle } from 'lucide-react';
import { getClasses, getIdCards } from '../api/client';
import Button from '../ui/Button';
import { Card, CardHeader } from '../ui/Card';
import { IdCard, IdCardSheet } from './IdCard';
import { sessionEndISO } from '../ui/format';

const inputCls = 'border border-line rounded-s px-3 py-2 text-sm bg-paper-raised text-ink focus:outline-none focus:ring-2 focus:ring-transit';

export default function IdCardsPanel({ onOpenStudent }) {
  const [classes,   setClasses]   = useState([]);
  const [classId,   setClassId]   = useState('');
  const [data,      setData]      = useState(null);
  const [selected,  setSelected]  = useState(new Set());
  const [validTill, setValidTill] = useState(sessionEndISO);
  const [print,     setPrint]     = useState(false);
  const [msg,       setMsg]       = useState('');

  useEffect(() => {
    getClasses().then(cs => { setClasses(cs); if (cs.length) setClassId(String(cs[0].id)); })
      .catch(err => setMsg('Error: ' + (err.response?.data?.error || err.message)));
  }, []);

  useEffect(() => {
    if (!classId) return;
    setData(null); setMsg('');
    getIdCards({ class_id: classId })
      .then(d => { setData(d); setSelected(new Set(d.students.map(s => s.id))); })
      .catch(err => setMsg('Error: ' + (err.response?.data?.error || err.message)));
  }, [classId]);

  function toggle(id) {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  }

  const chosen = data ? data.students.filter(s => selected.has(s.id)) : [];
  const missingPhoto = chosen.filter(s => !s.photo).length;

  return (
    <div>
      <h2 className="font-display font-extrabold text-xl text-ink mb-4">Student ID Cards</h2>

      <Card className="p-4 mb-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">Class
          <select className={inputCls} value={classId} onChange={e => setClassId(e.target.value)}>
            {!classes.length && <option value="">No classes yet</option>}
            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">Valid till
          <input type="date" className={inputCls} value={validTill} onChange={e => setValidTill(e.target.value)} />
        </label>
        <Button className="ml-auto flex items-center gap-1.5" disabled={!chosen.length} onClick={() => setPrint(true)}>
          <Printer size={14} /> Print {chosen.length} card{chosen.length === 1 ? '' : 's'}
        </Button>
      </Card>

      {msg && <p className="text-critical text-sm font-bold mb-3">{msg}</p>}
      {missingPhoto > 0 && (
        <p className="flex items-center gap-2 text-sm font-semibold text-caution bg-caution-tint px-3 py-2 rounded-s w-fit mb-3">
          <AlertTriangle size={15} /> {missingPhoto} selected student{missingPhoto === 1 ? ' has' : 's have'} no photo — open their profile to add one.
        </p>
      )}

      {data && (
        <div className="grid grid-cols-[minmax(260px,320px)_1fr] gap-5 items-start max-[900px]:grid-cols-1">
          <Card>
            <CardHeader title={`Students (${selected.size}/${data.students.length})`} action={
              <div className="flex gap-2 text-xs font-bold">
                <button className="text-transit-deep hover:underline" onClick={() => setSelected(new Set(data.students.map(s => s.id)))}>All</button>
                <button className="text-transit-deep hover:underline" onClick={() => setSelected(new Set())}>None</button>
              </div>
            } />
            {!data.students.length && <p className="px-5 py-6 text-sm text-ink-soft">No students in this class.</p>}
            {data.students.map(s => (
              <div key={s.id} className="flex items-center gap-3 px-5 py-2.5 border-t border-line-soft first:border-t-0">
                <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggle(s.id)} aria-label={`Include ${s.name}`}
                  className="w-4 h-4 accent-[var(--transit)]" />
                <button onClick={() => onOpenStudent(s.id)} className="text-sm font-semibold text-transit-deep hover:underline text-left flex-1 min-w-0 truncate">
                  {s.name}
                </button>
                {!s.photo && <span className="text-[11px] font-bold text-caution">No photo</span>}
              </div>
            ))}
          </Card>

          <div>
            <h3 className="text-[11px] font-bold uppercase tracking-[0.12em] text-ink-soft mb-2">Preview (actual card size when printed)</h3>
            <div className="flex flex-wrap gap-4">
              {chosen.map(s => <IdCard key={s.id} student={s} school={data.school} validTill={validTill} />)}
              {!chosen.length && <p className="text-sm text-ink-soft">Tick students on the left to preview their cards.</p>}
            </div>
          </div>
        </div>
      )}

      {print && <IdCardSheet students={chosen} school={data.school} validTill={validTill} onClose={() => setPrint(false)} />}
    </div>
  );
}
